import { useRef, useState } from "react";
import { Button, Input, Tooltip } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";

export type SidebarPackage = {
  id: string;
  sequence: string;
  title: string;
  statusLabel: string;
  bidPackageCount: number;
};

function bidPackageLabel(count: number) {
  if (count === 0) return "0 Bid Packages";
  return count === 1 ? "1 Bid Package" : `${count} Bid Packages`;
}

function SidebarPanelIcon(props: { collapsed?: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="0.75"
        y="0.75"
        width="12.5"
        height="12.5"
        rx="2.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect
        x={props.collapsed ? "8.25" : "3.75"}
        y="0.75"
        width="1.5"
        height="12.5"
        fill="currentColor"
      />
    </svg>
  );
}

const chipClass =
  "inline-flex h-[17px] max-w-[50%] min-w-0 items-center truncate rounded border border-solid border-ciq-gray-300 bg-ciq-gray-50 px-1.5 text-[10px] font-medium leading-none text-ciq-gray-900";

function TruncatedText(props: { text: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [open, setOpen] = useState(false);

  return (
    <Tooltip
      title={props.text}
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setOpen(false);
          return;
        }
        const el = ref.current;
        setOpen(Boolean(el && el.scrollWidth > el.clientWidth));
      }}
    >
      <span ref={ref} className={props.className}>
        {props.text}
      </span>
    </Tooltip>
  );
}

function CollapsedPackageButton(props: {
  item: SidebarPackage;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const { item, selected, onSelect } = props;

  // The collapsed rail shows only the sequence, so the tooltip always names
  // the package too.
  return (
    <Tooltip title={`${item.sequence} | ${item.title}`}>
      <Button
        data-testid={`planner-sidebar-number-${item.id}`}
        aria-label={item.title}
        aria-pressed={selected}
        className={`flex h-7 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md px-1 text-xs font-medium shadow-none ${
          selected
            ? "border-ciq-active-schedule-border text-ciq-active-schedule-border"
            : "border-ciq-gray-300 text-ciq-gray-900"
        }`}
        onClick={() => onSelect(item.id)}
      >
        <span className="block max-w-full truncate">{item.sequence}</span>
      </Button>
    </Tooltip>
  );
}

export default function DesignPackageSidebar(props: {
  open: boolean;
  onToggleOpen: () => void;
  packages: SidebarPackage[];
  totalCount: number;
  projectIsEmpty: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  query: string;
  onQueryChange: (query: string) => void;
  canAdd: boolean;
  onAdd: () => void;
}) {
  const {
    open,
    onToggleOpen,
    packages,
    totalCount,
    projectIsEmpty,
    selectedId,
    onSelect,
    query,
    onQueryChange,
    canAdd,
    onAdd
  } = props;
  const visibleCount = packages.length;
  const searching = query.trim().length > 0;
  const countLabel = searching
    ? `${visibleCount} of ${totalCount}`
    : `${visibleCount}`;

  if (!open) {
    return (
      <aside
        className="flex h-full w-14 shrink-0 flex-col border-0 border-r border-solid border-ciq-gray-150 bg-white"
        data-testid="planner-sidebar"
        data-open="false"
      >
        <div className="flex flex-col items-center gap-3 border-0 border-b border-solid border-ciq-gray-100 px-2 py-4">
          <button
            type="button"
            aria-label="Open Design Packages list"
            data-testid="planner-sidebar-toggle"
            className="inline-flex h-5 w-5 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-ciq-gray-900"
            onClick={onToggleOpen}
          >
            <SidebarPanelIcon collapsed />
          </button>
          <span
            className="inline-flex h-5 max-w-full min-w-6 items-center justify-center truncate rounded-full border border-solid border-ciq-gray-300 bg-white px-1.5 text-xs text-ciq-black-a45"
            data-testid="planner-sidebar-count"
          >
            {countLabel}
          </span>
        </div>
        <div className="flex min-h-0 flex-1 flex-col items-center gap-2 overflow-auto px-2 py-3">
          {packages.map((item) => (
            <CollapsedPackageButton
              key={item.id}
              item={item}
              selected={item.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </div>
        <div className="mt-auto border-0 border-t border-solid border-ciq-gray-100 p-3">
          <Button
            aria-label="Add New Design Package"
            data-testid="planner-sidebar-add"
            className="cursor-pointer"
            disabled={!canAdd}
            icon={<PlusOutlined />}
            onClick={onAdd}
          />
        </div>
      </aside>
    );
  }

  return (
    <aside
      className="flex h-full w-[300px] shrink-0 flex-col border-0 border-r border-solid border-ciq-gray-150 bg-white"
      data-testid="planner-sidebar"
      data-open="true"
    >
      <div className="flex flex-col gap-2.5 p-4">
        <div className="flex items-center">
          <div className="min-w-0 truncate text-sm font-semibold text-black">
            Design Packages
          </div>
          <span
            className="ml-2 inline-flex h-5 min-w-6 shrink-0 items-center justify-center rounded-full border border-solid border-ciq-gray-300 bg-white px-1.5 text-xs text-ciq-black-a45"
            data-testid="planner-sidebar-count"
          >
            {countLabel}
          </span>
          <button
            type="button"
            aria-label="Close Design Packages list"
            data-testid="planner-sidebar-toggle"
            className="ml-auto inline-flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-0 text-ciq-gray-900"
            onClick={onToggleOpen}
          >
            <SidebarPanelIcon />
          </button>
        </div>
        <Input
          allowClear
          prefix={<SearchOutlined className="text-ciq-gray-400" />}
          placeholder="Search Design Packages"
          data-testid="planner-sidebar-search"
          value={query}
          className="h-7 rounded-md [&_input::placeholder]:text-ciq-gray-400"
          onChange={(e) => onQueryChange(e.target.value)}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        {packages.length === 0 ? (
          <div className="px-4 py-2.5" data-testid="planner-sidebar-empty">
            <div className="truncate text-xs font-medium text-black">
              {projectIsEmpty
                ? "No design packages"
                : "No Design Packages match your search"}
            </div>
            {projectIsEmpty ? (
              <div className="mt-1 truncate text-xs text-ciq-gray-400">
                Design packages you create will be listed here.
              </div>
            ) : null}
          </div>
        ) : (
          <div className="border-0 border-b border-solid border-ciq-neutral-25">
            {packages.map((item) => {
              const selected = item.id === selectedId;
              return (
                <button
                  key={item.id}
                  type="button"
                  data-testid={`planner-sidebar-item-${item.id}`}
                  className={`flex w-full items-start gap-1.5 border-0 border-t border-solid border-ciq-neutral-25 px-4 py-2.5 text-left ${
                    selected ? "bg-ciq-primary-bg" : "bg-white"
                  }`}
                  onClick={() => onSelect(item.id)}
                >
                  <TruncatedText
                    text={item.sequence}
                    className={`w-8 shrink-0 truncate text-sm font-medium leading-5 ${
                      selected
                        ? "text-ciq-active-schedule-border"
                        : "text-ciq-gray-400"
                    }`}
                  />
                  <span className="min-w-0 flex-1">
                    <TruncatedText
                      text={item.title}
                      className={`block truncate text-sm font-medium leading-5 ${
                        selected
                          ? "text-ciq-active-schedule-border"
                          : "text-black"
                      }`}
                    />
                    <span className="mt-1.5 flex min-w-0 items-center gap-1.5">
                      <TruncatedText
                        text={item.statusLabel}
                        className={chipClass}
                      />
                      {item.bidPackageCount === 0 ? (
                        <TruncatedText
                          text={bidPackageLabel(0)}
                          className="min-w-0 truncate text-[10px] leading-none text-ciq-gray-700"
                        />
                      ) : (
                        <TruncatedText
                          text={bidPackageLabel(item.bidPackageCount)}
                          className="inline-flex h-[18px] min-w-0 max-w-[55%] items-center truncate rounded-full border border-solid border-ciq-gray-300 bg-white px-2 text-[10px] font-medium leading-none text-ciq-gray-900"
                        />
                      )}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div className="mt-auto border-0 border-t border-solid border-ciq-gray-100 px-4 py-3.5">
        <button
          type="button"
          data-testid="planner-sidebar-add"
          className="block max-w-full cursor-pointer truncate border-none bg-transparent p-0 text-left text-sm font-medium text-ciq-active-schedule-border disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!canAdd}
          onClick={onAdd}
        >
          + Add New
        </button>
      </div>
    </aside>
  );
}
